import React, { useEffect, useState, useCallback, useMemo } from "react";
import debounce from "lodash/debounce";
import { httpService } from "../../../services/httpService";
import { toast } from "react-toastify";
import { FaInfoCircle, FaTrash } from "react-icons/fa";

// Reusable toggle section wrapper
const ToggleSection = React.memo(
  ({
    label,
    active,
    onToggle,
    expanded,
    onExpand,
    children,
    info,
    headerAction,
  }) => {
    const handleExpand = (e) => {
      e.stopPropagation();
      if (active) onExpand?.();
    };

    const handleToggle = (e) => {
      e.stopPropagation();
      onToggle?.();
      if (!active) onExpand?.();
      else onExpand?.(false);
    };

    return (
      <div className="grid grid-cols-1 border border-gray-200 shadow rounded-lg">
        <div className="flex justify-between items-center bg-gray-50 p-2">
          <div className="flex items-center gap-4">
            <label>{label}</label>
            {onToggle &&
            <button
              type="button"
              aria-pressed={active}
              onClick={handleToggle}
              className={`relative inline-flex items-center h-6 rounded-full w-14 transition-colors duration-300 ${
                active ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block w-6 h-6 transform rounded-full bg-white shadow transition-transform duration-300 ${
                  active ? "translate-x-8" : "translate-x-0"
                }`}
              />
            </button>
            }
            {/* Info icon slot — renders after toggle if passed */}
            {info && info}
            {headerAction && active && headerAction}
          </div>

          <button
            onClick={handleExpand}
            className={`p-1 ${!active ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={!active}
          >
            <svg
              className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${
                expanded ? "rotate-180" : ""
              }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {expanded && active && <div className="bg-white p-2">{children}</div>}
      </div>
    );
  },
);

const PriceSurchargeForm = React.memo(
  ({
    isActive,
    data,
    updateData,
    onValidationChange,
    selectedPetTypes,
    serviceSelectedRoomTypes,
    serviceRoomTypeOptions,
    sizeCoatSelection = "only_coat",
  }) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const [availableSizes, setAvailableSizes] = useState([]);
    const hideSizeSurcharge = sizeCoatSelection === "only_coat";

    const [form, setForm] = useState({
      ...data,
      sizeOptions: data.sizeOptions || {},
      roomTypeOptions: data.roomTypeOptions || {},
      selectedRoomTypes: data.selectedRoomTypes || [],
      longStayOptions: data.longStayOptions || [],
      multipleBoardingDiscounts: data.multipleBoardingDiscounts || [],
      clientLabelDiscounts: data.clientLabelDiscounts || [],
    });
    const [roomTypes, setRoomTypes] = useState([]);
    const [discountInputFocus, setDiscountInputFocus] = useState({});
    const [discountTypeSelected, setDiscountTypeSelected] = useState({});
    const [showRoomTooltip, setShowRoomTooltip] = useState(false);

    useEffect(() => {
      if (isActive) {
        setForm({
          ...data,
          sizeOptions: data.sizeOptions || {},
          roomTypeOptions: data.roomTypeOptions || {},
          selectedRoomTypes: data.selectedRoomTypes || [],
          longStayOptions: data.longStayOptions || [],
          multipleBoardingDiscounts: data.multipleBoardingDiscounts || [],
          clientLabelDiscounts: data.clientLabelDiscounts || [],
        });
      }
    }, [data, isActive]);

    useEffect(() => {
      if (hideSizeSurcharge && form.priceBasedOnSize !== "no") {
        setForm((prev) => ({ ...prev, priceBasedOnSize: "no" }));
      }
    }, [hideSizeSurcharge, form.priceBasedOnSize]);

    const [expandedSections, setExpandedSections] = useState({});

    const validateDiscountSections = useCallback((formData) => {
      const errors = [];

      // Validate long stay discounts
      if (formData.priceDiscountForLongStay === "yes") {
        formData.longStayOptions.forEach((option) => {
          if (
            (option.discount && !option.discount_type) ||
            (!option.discount && option.discount_type)
          ) {
            errors.push(
              `Long Stay: ${option.no_of_day} has incomplete discount fields`,
            );
          }
        });
      }

      // Validate multiple boarding discounts
      if (formData.priceDiscountForMulitpleBoarding === "yes") {
        formData.multipleBoardingDiscounts.forEach((option) => {
          if (
            (option.discount && !option.discount_type) ||
            (!option.discount && option.discount_type)
          ) {
            errors.push(
              `Multiple Booking: ${option.no_of_pet} has incomplete discount fields`,
            );
          }
        });
      }

      // Validate client label discounts
      if (formData.priceDiscountForClientLabel === "yes") {
        formData.clientLabelDiscounts.forEach((option) => {
          if (
            (option.discount && !option.discount_type) ||
            (!option.discount && option.discount_type)
          ) {
            errors.push(
              `Client Label: ${option.label} has incomplete discount fields`,
            );
          }
        });
      }

      return errors;
    }, []);

    // Update validation when form changes
    useEffect(() => {
      const errors = validateDiscountSections(form);
      const hasErrors = errors.length > 0;
      setHasValidationErrors(hasErrors);

      // Pass validation status to parent
      onValidationChange?.(!hasErrors);

      // Show toast for first error (optional)
      if (hasErrors && isActive) {
        // You can show the first error or handle differently
        // toast.error(errors[0]);
      }
    }, [form, validateDiscountSections, isActive, onValidationChange]);

    // Keep sections expanded when toggle is enabled, close when disabled
    useEffect(() => {
      if (form.priceBasedOnSize === "yes") {
        setExpandedSections((prev) => ({ ...prev, size: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, size: false }));
      }
    }, [form.priceBasedOnSize]);

    useEffect(() => {
      if (form.priceBasedOnRoomType === "yes") {
        setExpandedSections((prev) => ({ ...prev, roomType: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, roomType: false }));
      }
    }, [form.priceBasedOnRoomType]);

    useEffect(() => {
      if (form.priceDiscountForLongStay === "yes") {
        setExpandedSections((prev) => ({ ...prev, longStay: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, longStay: false }));
      }
    }, [form.priceDiscountForLongStay]);

    useEffect(() => {
      if (form.priceDiscountForMulitpleBoarding === "yes") {
        setExpandedSections((prev) => ({ ...prev, multipleBoarding: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, multipleBoarding: false }));
      }
    }, [form.priceDiscountForMulitpleBoarding]);

    useEffect(() => {
      if (form.priceDiscountForClientLabel === "yes") {
        setExpandedSections((prev) => ({ ...prev, clientLabel: true }));
      } else {
        setExpandedSections((prev) => ({ ...prev, clientLabel: false }));
      }
    }, [form.priceDiscountForClientLabel]);
    const toggleSection = useCallback((section, value) => {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: typeof value === "boolean" ? value : !prev[section],
      }));
    }, []);

    // Fetch room types if needed
    useEffect(() => {
      if (isActive && form.priceBasedOnRoomType === "yes") {
        (async () => {
          try {
            setLoading(true);
            const { data: types } = await httpService.get("/room/getRooms");
            setRoomTypes(types);
          } catch {
            toast.error("Could not load room types");
          } finally {
            setLoading(false);
          }
        })();
      }
    }, [isActive, form.priceBasedOnRoomType]);

    useEffect(() => {
      const fetchPetTypes = async () => {
        try {

          const response = await httpService.get("/vendors/getPetTypes");
          const petTypesData = response.data;

          // If no pet types selected, show all enabled sizes
          if (!selectedPetTypes || selectedPetTypes.length === 0) {
            const enabledSizes = [];
            petTypesData.forEach((pet) => {
              if (pet.enabled) {
                pet.sizes
                  .filter((size) => size.enabled === true)
                  .forEach((size) => {
                    enabledSizes.push(size.size_label.toLowerCase());
                  });
              }
            });
            const uniqueSizes = [...new Set(enabledSizes)];
            const sizeOrder = ["small", "medium", "large", "giant"];
            const sortedSizes = sizeOrder.filter((size) =>
              uniqueSizes.includes(size),
            );
            setAvailableSizes(sortedSizes);
            return;
          }

          // Create a map of pet_type_id to pet name
          const petIdToNameMap = {};
          petTypesData.forEach((pet) => {
            petIdToNameMap[pet.pet_type_id] = pet.name?.toLowerCase();
          });

          // Convert selected pet IDs to names
          const selectedPetNames = selectedPetTypes
            .map((petId) => {
              // Handle both object and string/ID formats
              const id =
                typeof petId === "object"
                  ? petId.pet_type_id || petId._id
                  : petId;
              const name = petIdToNameMap[id];
              return name;
            })
            .filter((name) => name); // Remove undefined names

          if (selectedPetNames.length === 0) {
            setAvailableSizes([]);
            return;
          }

          const enabledSizes = [];
          petTypesData.forEach((pet) => {
            const petName = pet.name?.toLowerCase();

            if (selectedPetNames.includes(petName) && pet.enabled) {
              pet.sizes
                .filter((size) => size.enabled === true)
                .forEach((size) => {
                  enabledSizes.push(size.size_label.toLowerCase());
                });
            }
          });

          const uniqueSizes = [...new Set(enabledSizes)];
          const sizeOrder = ["small", "medium", "large", "giant"];
          const sortedSizes = sizeOrder.filter((size) =>
            uniqueSizes.includes(size),
          );

          setAvailableSizes(sortedSizes);
        } catch (error) {
          console.error("Failed to fetch pet types:", error);
          toast.error("Could not load pet size configurations");
        }
      };

      fetchPetTypes();
    }, [selectedPetTypes]);

    // Validation helper
    const validate = useCallback((field, value) => {
      setErrors((prev) => {
        const next = { ...prev };
        if (field === "name") {
          if (!value?.trim()) next.name = "Room Name is required";
          else if (value.length < 3)
            next.name = "Name must be at least 3 characters";
          else delete next.name;
        }
        return next;
      });
    }, []);

    // Debounced validation change
    const debouncedValidation = useMemo(
      () => debounce((isValid) => onValidationChange?.(isValid), 300),
      [onValidationChange],
    );
    useEffect(() => () => debouncedValidation.cancel(), [debouncedValidation]);
    useEffect(() => {
      const isValid =
        form.name?.trim() && form.category?.service_category_type_id;
      debouncedValidation(isValid);
    }, [form.name, form.category, debouncedValidation]);

    useEffect(() => {
      setForm((prev) => {
        const roomIds = serviceSelectedRoomTypes || [];
        const options = {};
        roomIds.forEach((id) => {
          // Preserve whatever the user already typed here first.
          // Only fall back to Step 1's default (or "0") for brand-new rooms.
          options[id] =
            prev.roomTypeOptions?.[id] ?? serviceRoomTypeOptions?.[id] ?? "0";
        });
        return {
          ...prev,
          selectedRoomTypes: roomIds,
          roomTypeOptions: options,
        };
      });
    }, [serviceSelectedRoomTypes]);

    const clampNonNegative = useCallback((val) => {
      const n = Number(val);
      return isNaN(n) || n < 0 ? "" : String(n);
    }, []);

    // Handlers
    const handleChange = useCallback(
      (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        validate(field, value);
      },
      [validate],
    );

    const handleSizePrice = useCallback(
      (size, val) => {
        setForm((prev) => ({
          ...prev,
          sizeOptions: { ...prev.sizeOptions, [size]: clampNonNegative(val) },
        }));
      },
      [clampNonNegative],
    );

    const handleRoomTypePrice = useCallback(
      (id, val) => {
        setForm((prev) => ({
          ...prev,
          roomTypeOptions: {
            ...prev.roomTypeOptions,
            [id]: clampNonNegative(val),
          },
        }));
      },
      [clampNonNegative],
    );

    useEffect(() => {
      if (isActive) {
        (async () => {
          try {
            setLoading(true);
            const { data: types } = await httpService.get("/room/getRooms");
            setRoomTypes(types?.data ?? types ?? []);
          } catch {
            toast.error("Could not load room types");
          } finally {
            setLoading(false);
          }
        })();
      }
    }, [isActive]);

    // Handle discount input focus with validation
    const handleDiscountInputFocus = (section, identifier) => {
      const key = `${section}-${identifier}`;
      setDiscountInputFocus((prev) => ({ ...prev, [key]: true }));

      // Check if discount type is selected
      if (!discountTypeSelected[key]) {
        toast.error("Please select a discount type first");
      }
    };

    const handleDiscountInputBlur = (section, identifier) => {
      const key = `${section}-${identifier}`;
      setDiscountInputFocus((prev) => ({ ...prev, [key]: false }));
    };

    const handleDiscountTypeChange = (section, identifier, discountType) => {
      const key = `${section}-${identifier}`;
      setDiscountTypeSelected((prev) => ({ ...prev, [key]: true }));
    };

    const handleLongStayOptionChange = useCallback((days, field, value) => {
      setForm((prev) => {
        const options = [...prev.longStayOptions];
        const idx = options.findIndex((opt) => opt.no_of_day === days);
        const entry =
          idx > -1
            ? { ...options[idx] }
            : { no_of_day: days, discount_type: "", discount: "" };
        entry[field] = value;

        // Update discount type selection state
        if (field === "discount_type" && value) {
          const key = `longStay-${days}`;
          setDiscountTypeSelected((prev) => ({ ...prev, [key]: true }));
        }

        if (idx > -1) options[idx] = entry;
        else options.push(entry);
        return { ...prev, longStayOptions: options };
      });
    }, []);

    const handleMultipleBoardingChange = useCallback((num, type, val) => {
      setForm((prev) => {
        const list = [...prev.multipleBoardingDiscounts];
        const idx = list.findIndex((i) => i.no_of_pet === num);
        const entry =
          idx > -1
            ? { ...list[idx] }
            : { no_of_pet: num, discount_type: "", discount: "" };
        entry.discount_type = type;
        entry.discount = val;

        // Update discount type selection state
        if (type) {
          const key = `multipleBoarding-${num}`;
          setDiscountTypeSelected((prev) => ({ ...prev, [key]: true }));
        }

        if (idx > -1) list[idx] = entry;
        else list.push(entry);
        return { ...prev, multipleBoardingDiscounts: list };
      });
    }, []);

    const handleClientLabelChange = useCallback((label, type, val) => {
      setForm((prev) => {
        const list = [...prev.clientLabelDiscounts];
        const idx = list.findIndex((i) => i.label === label);
        const entry =
          idx > -1
            ? { ...list[idx] }
            : { label: label, discount_type: "", discount: "" };
        entry.discount_type = type;
        entry.discount = val;

        // Update discount type selection state
        if (type) {
          const key = `clientLabel-${label}`;
          setDiscountTypeSelected((prev) => ({ ...prev, [key]: true }));
        }

        if (idx > -1) list[idx] = entry;
        else list.push(entry);
        return { ...prev, clientLabelDiscounts: list };
      });
    }, []);

    // Debounced update to parent
    const debouncedUpdate = useMemo(
      () => debounce(updateData, 300),
      [updateData],
    );
    useEffect(() => {
      debouncedUpdate(form);
      return () => debouncedUpdate.cancel();
    }, [form, debouncedUpdate]);

    if (loading)
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      );

    const dayRanges = ["5-10 Days", ">10 Days"];
    const getLongStayEntry = (days) =>
      form.longStayOptions.find((o) => o.no_of_day === days) || {};
    const getBoardingEntry = (num) =>
      form.multipleBoardingDiscounts.find((o) => o.no_of_pet === num) || {};
    const clientLabels = [
      "VIP Client",
      "New Client",
      "Regular Client",
      "Premium Client",
      "Frequent Visitor",
    ]; // ideally passed as prop

    return (
      <div className="space-y-4">
        {!hideSizeSurcharge && (
          <ToggleSection
            label="Set Price Surcharge based on size"
            active={form.priceBasedOnSize === "yes"}
            onToggle={() =>
              handleChange(
                "priceBasedOnSize",
                form.priceBasedOnSize === "yes" ? "no" : "yes",
              )
            }
            expanded={expandedSections.size}
            onExpand={() => toggleSection("size")}
            availableSizes={availableSizes}
          >
            {availableSizes.length > 0 ? (
              availableSizes.map((size) => (
                <div key={size} className="flex items-center gap-2 mb-2">
                  <label
                    htmlFor={`size-${size}`}
                    className="w-[10%] capitalize"
                  >
                    {size}
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      ₹
                    </span>
                    <input
                      id={`size-${size}`}
                      type="number"
                      min={0}
                      placeholder="Enter price"
                      className="block pl-8 pr-2 py-2 border border-gray-300 rounded"
                      value={form.sizeOptions[size] || ""}
                      onChange={(e) => handleSizePrice(size, e.target.value)}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">
                Loading size options...
              </div>
            )}
          </ToggleSection>
        )}
        <ToggleSection
          label="Set Price Surcharge Based On Room Type"
          info={
            <div style={{ position: "relative", display: "inline-block" }}>
              <FaInfoCircle
                className="text-gray-500 cursor-help hover:text-gray-700"
                style={{ fontSize: "14px" }}
                onMouseEnter={() => setShowRoomTooltip(true)}
                onMouseLeave={() => setShowRoomTooltip(false)}
              />
              {showRoomTooltip && (
                <div
                  style={{
                    position: "fixed",
                    zIndex: 9999,
                    backgroundColor: "#1f2937",
                    color: "white",
                    fontSize: "11px",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    width: "260px",
                    pointerEvents: "none",
                    transform: "translateY(-110%)",
                    marginTop: "-8px",
                  }}
                >
                  Rooms are added against each Pet Type in the Service Details
                  step. Leave a price as 0 for no extra surcharge.
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-6px",
                      left: "20px",
                      borderLeft: "6px solid transparent",
                      borderRight: "6px solid transparent",
                      borderTop: "6px solid #1f2937",
                    }}
                  />
                </div>
              )}
            </div>
          }
          active={form.priceBasedOnRoomType === "yes"}
          // onToggle={() =>
          //   handleChange(
          //     "priceBasedOnRoomType",
          //     form.priceBasedOnRoomType === "yes" ? "no" : "yes",
          //   )
          // }
          expanded={expandedSections.roomType}
          onExpand={() => toggleSection("roomType")}
        >
          {form.selectedRoomTypes?.length > 0 ? (
            form.selectedRoomTypes.map((roomId) => {
              const rt = roomTypes.find((r) => r._id === roomId);
              return (
                <div key={roomId} className="flex items-center gap-2 mb-2">
                  <div className="w-[30%] capitalize">
                    {rt?.room_name || "Room"}
                  </div>
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      ₹
                    </span>
                    <input
                      id={`room-${roomId}`}
                      type="number"
                      min={0}
                      placeholder="0"
                      className="block pl-8 pr-2 py-2 border border-gray-300 rounded"
                      value={form.roomTypeOptions?.[roomId] ?? "0"}
                      onChange={(e) =>
                        handleRoomTypePrice(roomId, e.target.value)
                      }
                      onBlur={(e) => {
                        if (e.target.value === "")
                          handleRoomTypePrice(roomId, "0");
                      }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-sm italic text-gray-500">
              No rooms selected yet — go back to Service Details to add rooms.
            </div>
          )}
        </ToggleSection>
        {/* 
        <ToggleSection
          label="Set Price Discount Based On Long Stay"
          active={form.priceDiscountForLongStay === "yes"}
          onToggle={() =>
            handleChange(
              "priceDiscountForLongStay",
              form.priceDiscountForLongStay === "yes" ? "no" : "yes",
            )
          }
          expanded={expandedSections.longStay}
          onExpand={() => toggleSection("longStay")}
        >
          <div className="flex flex-col gap-2">
            {dayRanges.map((range) => {
              const ent = getLongStayEntry(range);
              const key = `longStay-${range}`;
              const hasDiscountType = discountTypeSelected[key];
              const isDiscountInputFocused = discountInputFocus[key];

              return (
                <div key={range} className="flex items-center gap-2 mb-2">
                  <label className="w-[12%] capitalize">{range}</label>
                  <select
                    className={`w-[22%] p-2 border rounded ${
                      hasDiscountType
                        ? "border-green-500"
                        : isDiscountInputFocused
                          ? "border-red-500"
                          : "border-gray-300"
                    }`}
                    value={ent.discount_type || ""}
                    onChange={(e) =>
                      handleLongStayOptionChange(
                        range,
                        "discount_type",
                        e.target.value,
                      )
                    }
                  >
                    <option value="">Discount Type</option>
                    <option value="value">Value</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    placeholder="Discount"
                    className={`w-[15%] p-2 border rounded ${
                      hasDiscountType
                        ? "border-green-500"
                        : isDiscountInputFocused
                          ? "border-red-500"
                          : "border-gray-300"
                    }`}
                    value={ent.discount || ""}
                    onChange={(e) =>
                      handleLongStayOptionChange(
                        range,
                        "discount",
                        e.target.value,
                      )
                    }
                    onFocus={() => handleDiscountInputFocus("longStay", range)}
                    onBlur={() => handleDiscountInputBlur("longStay", range)}
                  />
                </div>
              );
            })}
          </div>
        </ToggleSection>

        <ToggleSection
          label="Set Price Discount for multiple Bookings"
          active={form.priceDiscountForMulitpleBoarding === "yes"}
          onToggle={() =>
            handleChange(
              "priceDiscountForMulitpleBoarding",
              form.priceDiscountForMulitpleBoarding === "yes" ? "no" : "yes",
            )
          }
          expanded={expandedSections.multipleBoarding}
          onExpand={() => toggleSection("multipleBoarding")}
        >
          <div className="flex flex-col gap-2">
            {["2", "3-4", "5+"].map((num) => {
              const ent = getBoardingEntry(num);
              const key = `multipleBoarding-${num}`;
              const hasDiscountType = discountTypeSelected[key];
              const isDiscountInputFocused = discountInputFocus[key];

              return (
                <div key={num} className="flex items-center gap-2 mb-2">
                  <div className="w-[18%] capitalize">{num} Booking</div>
                  <select
                    className={`w-[22%] p-2 border rounded ${
                      hasDiscountType
                        ? "border-green-500"
                        : isDiscountInputFocused
                          ? "border-red-500"
                          : "border-gray-300"
                    }`}
                    value={ent.discount_type || ""}
                    onChange={(e) =>
                      handleMultipleBoardingChange(
                        num,
                        e.target.value,
                        ent.discount,
                      )
                    }
                  >
                    <option value="">Discount Type</option>
                    <option value="value">Value</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    placeholder="Discount"
                    className={`w-[15%] p-2 border rounded ${
                      hasDiscountType
                        ? "border-green-500"
                        : isDiscountInputFocused
                          ? "border-red-500"
                          : "border-gray-300"
                    }`}
                    value={ent.discount || ""}
                    onChange={(e) =>
                      handleMultipleBoardingChange(
                        num,
                        ent.discount_type,
                        e.target.value,
                      )
                    }
                    onFocus={() =>
                      handleDiscountInputFocus("multipleBoarding", num)
                    }
                    onBlur={() =>
                      handleDiscountInputBlur("multipleBoarding", num)
                    }
                  />
                </div>
              );
            })}
          </div>
        </ToggleSection>

        <ToggleSection
          label="Set Price Discount Based On Client Label"
          active={form.priceDiscountForClientLabel === "yes"}
          onToggle={() =>
            handleChange(
              "priceDiscountForClientLabel",
              form.priceDiscountForClientLabel === "yes" ? "no" : "yes",
            )
          }
          expanded={expandedSections.clientLabel}
          onExpand={() => toggleSection("clientLabel")}
        >
          <div className="flex flex-col gap-2">
            {clientLabels.map((label) => {
              const ent =
                form.clientLabelDiscounts.find((i) => i.label === label) || {};
              const key = `clientLabel-${label}`;
              const hasDiscountType = discountTypeSelected[key];
              const isDiscountInputFocused = discountInputFocus[key];

              return (
                <div key={label} className="flex items-center gap-2 mb-2">
                  <div className="mr-2">{label}</div>
                  <select
                    className={`w-[22%] p-2 border rounded ${
                      hasDiscountType
                        ? "border-green-500"
                        : isDiscountInputFocused
                          ? "border-red-500"
                          : "border-gray-300"
                    }`}
                    value={ent.discount_type || ""}
                    onChange={(e) =>
                      handleClientLabelChange(
                        label,
                        e.target.value,
                        ent.discount,
                      )
                    }
                  >
                    <option value="">Discount Type</option>
                    <option value="value">Value</option>
                    <option value="percentage">Percentage</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    placeholder="Discount"
                    className={`w-[15%] p-2 border rounded ${
                      hasDiscountType
                        ? "border-green-500"
                        : isDiscountInputFocused
                          ? "border-red-500"
                          : "border-gray-300"
                    }`}
                    value={ent.discount || ""}
                    onChange={(e) =>
                      handleClientLabelChange(
                        label,
                        ent.discount_type,
                        e.target.value,
                      )
                    }
                    onFocus={() =>
                      handleDiscountInputFocus("clientLabel", label)
                    }
                    onBlur={() => handleDiscountInputBlur("clientLabel", label)}
                  />
                </div>
              );
            })}
          </div>
        </ToggleSection>*/}
      </div>
    );
  },
);

export default PriceSurchargeForm;
